import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import {
  Purchases,
  LOG_LEVEL,
  PurchasesEntitlementInfo,
  PAYWALL_RESULT,
} from '@revenuecat/purchases-capacitor';
import { RevenueCatUI } from '@revenuecat/purchases-capacitor-ui';

import { ApiService, APIRoutes } from './api.service';

/**
 * Interface to define the structure of subscription information.
 */
interface SubscriptionInfo {
  isActive: boolean;
  willRenew: boolean;
  expiresDate: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class InAppPurchaseService {
  // Flag to check if the application is running on a Capacitor (native) platform
  private isCapacitor: boolean;

  constructor(private platform: Platform, private apiService: ApiService) {
    // Determine if the app is running on a Capacitor platform during service instantiation
    this.isCapacitor = this.platform.is('capacitor');
  }

  /**
   * Initializes the RevenueCat SDK. This should only be called on native platforms.
   */
  async initialize() {
    if (this.isCapacitor) {
      const apiKey = this.platform.is('ios')
        ? 'appl_kmcqKOwujPkIitiDsVBTWeOqkaG'
        : 'goog_VvXdsgzDYfTtKFLpAQNzhYoNeqn';

      this.platform
        .ready()
        .then(async () => {
          await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
          await Purchases.configure({
            apiKey: apiKey,
          });
        })
        .catch((error) => {
          console.error(
            'Failed to initialize RevenueCat on Capacitor platform:',
            error
          );
        });
    } else {
      console.warn(
        'RevenueCat In-App Purchase functionality is not available on web platform. Initialization skipped.'
      );
    }
  }

  async presentPaywall(): Promise<boolean> {
    try {
      const offerings = await Purchases.getOfferings();
      console.log('Offerings:', offerings);

      if (!offerings.current) {
        console.error('No current offering available');
        return false;
      }

      // Present paywall for current offering:
      const { result } = await RevenueCatUI.presentPaywall();

      switch (result) {
        case PAYWALL_RESULT.NOT_PRESENTED:
        case PAYWALL_RESULT.ERROR:
        case PAYWALL_RESULT.CANCELLED:
          return false;
        case PAYWALL_RESULT.PURCHASED:
        case PAYWALL_RESULT.RESTORED:
          return true;
        default:
          return false;
      }
    } catch (error) {
      console.error('Error presenting paywall:', error);
      return false;
    }
  }

  async getOfferings() {
    try {
      const result = await Purchases.getOfferings();
      console.log('Full offerings result:', JSON.stringify(result, null, 2));
      return result;
    } catch (error) {
      console.error('Error getting offerings:', JSON.stringify(error, null, 2));
      throw error;
    }
  }
}
